import api from '@/lib/api';
import { API_ENDPOINTS } from '@/constants/endpoints';
import type { ApiResponse } from '@/types';

/**
 * Settings › Logs › Deviations: sent replies that failed the pre-send quality check (backend
 * `sentinel_send_gate_checks`). From the Sentinel workspace, a Send that overrode a pause; from the
 * extension, a reply checked after its helpdesk webhook, on the text the agent sent.
 */

/** How the log is ordered: newest first, or by Edit %. */
export type DeviationSort = 'recent' | 'editRate.desc' | 'editRate.asc';

/** Where the reply was sent from. */
export type DeviationSentVia = 'workspace' | 'extension';

export type DeviationWritingIssueKind = 'spelling' | 'grammar' | 'tone' | 'formatting';

/** One writing issue the check found: the kind and the words it is about. */
export interface DeviationWritingIssue {
	kind: DeviationWritingIssueKind;
	text: string;
	probability?: number;
}

/** One row of the log (backend shadowReport.utils.ts `toGateDeviationEntry`). */
export interface Deviation {
	checkId: string;
	ticketId: string;
	/** The ticket in its helpdesk; null when no link can be built. */
	ticketUrl: string | null;
	sentAt: string;
	intent: string | null;
	sentBy: string | null;
	sentVia: DeviationSentVia;
	/** 1–5; null when the AI check did not score the reply (a writing issue flagged it on its own). */
	aiScore: number | null;
	/** The brand's threshold the score was judged against. */
	blockBelow: number;
	writingIssues: DeviationWritingIssue[];
	/** Share of words the draft and the sent reply do not share, 0–100; null without both. */
	editRate: number | null;
	reviewed: boolean;
}

export interface DeviationPage {
	entries: Deviation[];
	hasMore: boolean;
	/** Passed back as `before` for the next page. */
	nextCursor: string | null;
}

/** One sender for the Sent by filter: their email (lower-cased), where they sent from most, and their count. */
export interface DeviationSender {
	email: string;
	via: DeviationSentVia;
	replies: number;
}

/** The Sent by and Intent options for one brand and date window. */
export interface DeviationFilterOptions {
	senders: DeviationSender[];
	intents: string[];
}

export interface DeviationFilter {
	/** A ticket id, matched exactly. */
	q?: string;
	/** ISO instants: sent at or after `from`, before `to`. */
	from?: string;
	to?: string;
	sentVia?: DeviationSentVia;
	/** Comma lists: any of these senders or intents. */
	sender?: string;
	intent?: string;
	/** Edit % bounds, inclusive, whole percents. */
	editRateMin?: number;
	editRateMax?: number;
	/** Reviewed rows only, or not reviewed yet; absent = both. */
	reviewed?: 'true' | 'false';
	sort?: DeviationSort;
}

export const DEVIATION_REVIEW_VERDICTS = ['kim', 'agent', 'style', 'both'] as const;
export type DeviationReviewVerdict = (typeof DEVIATION_REVIEW_VERDICTS)[number];

export interface DeviationReview {
	verdict: DeviationReviewVerdict;
	note: string | null;
	reviewedBy: string | null;
	reviewedAt: string;
}

/** A factor of the AI score, as the send gate words it. */
export interface DeviationFactor {
	key: string;
	label: string;
	level: number;
	levelText: string;
	confidence: number;
}

/** One deviation's drawer (backend `toGateDeviationDetail`). */
export interface DeviationDetail {
	checkId: string;
	ticketId: string;
	ticketUrl: string | null;
	sentAt: string;
	sentVia: DeviationSentVia;
	sentBy: string | null;
	buId: number | null;
	channel: string;
	intent: string | null;
	/** The customer message the AI draft answered. */
	customerMessage: { text: string; sentAt: string | null } | null;
	draft: string | null;
	sentText: string | null;
	/** The draft's retrieved knowledge (sources, articles, SOPs). */
	knowledgeBaseIds: string[];
	aiScore: number | null;
	blockBelow: number;
	factors: DeviationFactor[];
	writingIssues: DeviationWritingIssue[];
	editRate: number | null;
	sharedWords: number | null;
	totalWords: number | null;
	/** The Sentinel's reason for sending past the pause; null for an extension reply. */
	overrideReason: string | null;
	review: DeviationReview | null;
}

const PAGE_SIZE = 25;

type BuScope = { bu_id?: string };

export const deviationService = {
	/** A page of the brand's deviations, in the filter's order; `before` is the previous page's cursor. */
	async fetchPage(filter: DeviationFilter, before: string | undefined, scope: BuScope) {
		const res = await api.get<ApiResponse<DeviationPage>>(API_ENDPOINTS.REPORTS.DEVIATIONS, {
			params: {
				...scope,
				...filter,
				...(before && { before }),
				limit: PAGE_SIZE
			}
		});
		return res.data.data;
	},

	/** The Sent by and Intent options for the brand in the date window. */
	async fetchFilterOptions(window: Pick<DeviationFilter, 'from' | 'to'>, scope: BuScope) {
		const res = await api.get<ApiResponse<DeviationFilterOptions>>(API_ENDPOINTS.REPORTS.DEVIATION_FILTER_OPTIONS, {
			params: { ...scope, ...window }
		});
		return res.data.data;
	},

	/** One deviation's drawer. */
	async fetchDetail(checkId: string, scope: BuScope) {
		const res = await api.get<ApiResponse<DeviationDetail>>(API_ENDPOINTS.REPORTS.DEVIATION(checkId), { params: scope });
		return res.data.data;
	},

	/** Save the reviewer's verdict and note on a deviation. */
	async saveReview(checkId: string, review: { verdict: DeviationReviewVerdict; note: string | null }, scope: BuScope) {
		const res = await api.patch<ApiResponse<DeviationReview>>(API_ENDPOINTS.REPORTS.DEVIATION_REVIEW(checkId), review, {
			params: scope
		});
		return res.data.data;
	}
};
